'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import AncTierForm from './AncTierForm';
import AncLogsTable from './AncLogsTable';
import AncSetOverview from './AncSetOverview';
import { Layers } from 'lucide-react';

type Tab = 'log' | 'history' | 'sets';

export default function AncSystemScreen() {
  const [activeTab, setActiveTab] = useState<Tab>('log');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'log', label: 'Log Activity' },
    { id: 'history', label: 'History' },
    { id: 'sets', label: 'ANC Sets' },
  ];

  return (
    <AppLayout>
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
            <Layers size={18} className="text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-foreground leading-none">ANC System</h1>
            <p className="text-xs text-muted-foreground mt-0.5">3-Tier Container Management</p>
          </div>
        </div>

        {/* Tier legend */}
        <div className="flex gap-2 mt-3 mb-1">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-50 border border-green-200 flex-1 justify-center">
            <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-green-700">Top</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 flex-1 justify-center">
            <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-700">Middle</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 flex-1 justify-center">
            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-blue-700">Bottom</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 pb-3">
        <div className="flex bg-muted rounded-xl p-1 gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pb-24 space-y-4">
        {activeTab === 'log' && (
          <AncTierForm onSaved={() => setRefreshTrigger((v) => v + 1)} />
        )}
        {activeTab === 'history' && (
          <AncLogsTable refreshTrigger={refreshTrigger} />
        )}
        {activeTab === 'sets' && (
          <AncSetOverview />
        )}
      </div>
    </AppLayout>
  );
}
